import { z } from "zod";
import { STEP_NAMES } from "./steps.js";

export const FAILURE_CLASSES = [
  "county_config_missing",
  "site_unreachable",
  "login_failed",
  "selector_missing",
  "search_form_changed",
  "date_entry_failed",
  "document_type_missing",
  "search_submit_failed",
  "result_parse_failed",
  "no_results_unconfirmed",
  "document_open_failed",
  "viewer_changed",
  "download_failed",
  "pdf_invalid",
  "upload_failed",
  "upload_verification_failed",
  "duplicate_detected",
  "rate_limited",
  "captcha_or_blocked",
  "browser_crashed",
  "engine_error",
  "timeout",
  "unknown_exception"
] as const;

export const FAILURE_SEVERITIES = ["info", "warning", "error", "critical"] as const;
export const RECOVERY_ACTIONS = ["retry", "config_change", "adapter_change", "human_review", "disable_county"] as const;

export type FailureClass = (typeof FAILURE_CLASSES)[number];
export type FailureSeverity = (typeof FAILURE_SEVERITIES)[number];
export type RecoveryAction = (typeof RECOVERY_ACTIONS)[number];

const retryableFailures = new Set<FailureClass>([
  "site_unreachable",
  "download_failed",
  "upload_failed",
  "rate_limited",
  "browser_crashed",
  "timeout",
  "engine_error"
]);

const humanReviewFailures = new Set<FailureClass>([
  "selector_missing",
  "search_form_changed",
  "document_type_missing",
  "result_parse_failed",
  "viewer_changed",
  "pdf_invalid",
  "captcha_or_blocked",
  "upload_verification_failed",
  "unknown_exception"
]);

export const ArtifactRefSchema = z.object({
  type: z.enum(["pdf", "screenshot", "html", "log", "network_trace", "manifest", "download_listing", "other"]),
  uri: z.string().min(1),
  mediaType: z.string().optional(),
  sha256: z.string().optional(),
  sizeBytes: z.number().int().nonnegative().optional()
});

export type ArtifactRef = z.infer<typeof ArtifactRefSchema>;

export const FailureRecordSchema = z.object({
  id: z.string().min(1),
  runId: z.string().min(1),
  county: z.string().min(1),
  step: z.enum(STEP_NAMES),
  failureClass: z.enum(FAILURE_CLASSES),
  severity: z.enum(FAILURE_SEVERITIES),
  message: z.string().min(1),
  retryable: z.boolean(),
  recommendedAction: z.enum(RECOVERY_ACTIONS),
  occurredAt: z.string().datetime(),
  url: z.string().optional(),
  engineType: z.string().optional(),
  engineVersion: z.string().optional(),
  stack: z.string().optional(),
  artifactRefs: z.array(ArtifactRefSchema).default([])
});

export type FailureRecord = z.infer<typeof FailureRecordSchema>;

export function isRetryableFailure(failureClass: FailureClass): boolean {
  return retryableFailures.has(failureClass);
}

export function recommendedActionForFailure(failureClass: FailureClass): RecoveryAction {
  if (isRetryableFailure(failureClass)) return "retry";
  if (failureClass === "county_config_missing") return "config_change";
  if (humanReviewFailures.has(failureClass)) return "human_review";
  if (failureClass === "login_failed") return "human_review";
  return "adapter_change";
}

export function severityForFailure(failureClass: FailureClass): FailureSeverity {
  if (failureClass === "captcha_or_blocked" || failureClass === "pdf_invalid") return "critical";
  if (failureClass === "no_results_unconfirmed" || failureClass === "duplicate_detected") return "warning";
  return "error";
}

export function buildFailureRecord(input: {
  runId: string;
  county: string;
  step: FailureRecord["step"];
  failureClass: FailureClass;
  message: string;
  occurredAt?: Date;
  url?: string;
  engineType?: string;
  engineVersion?: string;
  stack?: string;
  artifactRefs?: ArtifactRef[];
}): FailureRecord {
  const occurredAt = input.occurredAt ?? new Date();
  return FailureRecordSchema.parse({
    id: `${input.runId}:${input.county}:${input.step}:${input.failureClass}:${occurredAt.getTime()}`,
    runId: input.runId,
    county: input.county,
    step: input.step,
    failureClass: input.failureClass,
    severity: severityForFailure(input.failureClass),
    message: input.message,
    retryable: isRetryableFailure(input.failureClass),
    recommendedAction: recommendedActionForFailure(input.failureClass),
    occurredAt: occurredAt.toISOString(),
    url: input.url,
    engineType: input.engineType,
    engineVersion: input.engineVersion,
    stack: input.stack,
    artifactRefs: input.artifactRefs ?? []
  });
}

export function classifyUnknownError(error: unknown): FailureClass {
  if (
    error instanceof Error &&
    /timeout|timed out|ERR_CONNECTION_TIMED_OUT|ETIMEDOUT|ERR_CONNECTION_RESET|ECONNRESET|connection reset/i.test(
      error.message
    )
  ) {
    return "timeout";
  }
  if (error instanceof Error && /PDF capture is not implemented/i.test(error.message)) return "document_open_failed";
  if (error instanceof Error && /captcha|blocked/i.test(error.message)) return "captcha_or_blocked";
  if (error instanceof Error && /login|guest|public access/i.test(error.message)) return "login_failed";
  if (error instanceof Error && /download/i.test(error.message)) return "download_failed";
  return "unknown_exception";
}
